import { logger } from "@/utils/logger";
import { createRoot, type Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { FormProvider } from './contexts/FormContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { DealerSessionProvider } from './contexts/DealerSessionContext';
import { readDealerCookie, storeDealerData, getDealerID } from './utils/dealerDataUtils';
import { readContextFromHTML, initializeDealerDataAndUTM, detectAndStoreVDPJson } from './utils/htmlContextUtils';
import { DealerManager } from './utils/dealerManager';
import { checkReloadType, logSuspiciousReload } from './utils/reloadDetection';
import ChatInterface from './components/ChatInterface';
import { PortalRootContext } from './contexts/PortalRootContext';
import { widgetDiagBeacon, installWidgetErrorListeners } from './utils/widgetDiagnostics';
import './index.css';
import './utils/leadPushDebug';

// Report uncaught widget errors (and unhandled rejections) to
// public.widget_load_diagnostics. This was previously wired only into the admin
// SPA (src/main.tsx), so production crashes inside the embedded widget — the one
// build we cannot open a console on — were never captured anywhere.
installWidgetErrorListeners();
widgetDiagBeacon('embed-script-loaded');

// Widget configuration interface
interface ChatWidgetConfig {
  containerId?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  theme?: 'light' | 'dark';
  apiEndpoint?: string;
}

// Guards against mounting a second React root onto the same container, which is
// reachable when a host page both lets the script auto-initialize and calls
// window.initChatWidget() itself.
let activeRoot: Root | null = null;
let activeContainerId: string | null = null;

class ChatWidget {
  private config: ChatWidgetConfig;
  private container: HTMLElement | null = null;
  private queryClient: QueryClient;
  private initialLanguage: 'fr' | 'en' = 'en';
  private root: Root | null = null;

  constructor(config: ChatWidgetConfig = {}) {
    // Block chat on "requests", "demandes", or "service" pages
    const currentPath = window.location.pathname.toLowerCase();
    const currentSearch = window.location.search.toLowerCase();
    const fullUrl = (currentPath + currentSearch).toLowerCase();
    if (fullUrl.includes('requests') || fullUrl.includes('demandes') || fullUrl.includes('service')) {
      logger.log('🚫 Widget blocked: URL contains "requests", "demandes", or "service"');
      return;
    }

    // CRITICAL SECURITY CHECK - Block unauthorized domains silently  
    const currentDomain = window.location.hostname;
    logger.log('🔒 WIDGET SECURITY CHECK:', currentDomain);
    
    // Note: Domain authorization is now handled by Supabase dealer authentication
    // The widget will load but the chat will fail gracefully if domain is not authorized

    // Check reload type immediately when widget is created
    const reloadType = checkReloadType();
    
    // CRITICAL: Detect and store VDP JSON data immediately
    detectAndStoreVDPJson();

    // Proactively purge the deprecated 1-year `d2cchatbotid` cookie on every widget load.
    import('./utils/cookieUtils').then(({ deleteCookie }) => deleteCookie('d2cchatbotid'));
    
    this.config = {
      containerId: 'chat-widget-container',
      position: 'bottom-right',
      theme: 'light',
      ...config
    };
    this.queryClient = new QueryClient();
    
    // CRITICAL: Initialize dealer data and UTM tracker FIRST
    // Note: This is intentionally not awaited to avoid blocking widget initialization
    initializeDealerDataAndUTM().catch(error => 
      console.error('❌ Failed to initialize dealer data and UTM:', error)
    );
    
    // Read all HTML context
    const htmlContext = readContextFromHTML();
    this.initialLanguage = htmlContext.initialLanguage;
    
    // Initialize dealer authentication and only proceed if authorized
    this.initializeDealerAuth().then((isAuthorized) => {
      if (isAuthorized) {
        this.init();
      } else {
        logger.log('🚫 Widget blocked: Unauthorized domain');
        // Optionally, you can display a subtle message or do nothing (silent fail)
      }
    });
  }

  private async initializeDealerAuth(): Promise<boolean> {
    try {
      logger.log('🔐 Initializing dealer authentication...');
      
      // Always clear cache for testing - check domain freshly each time
      logger.log('🔄 Clearing cache and checking domain authorization...');
      
      // Clear any existing cache to ensure fresh domain check
      const { DealerCache } = await import('./utils/dealerCache');
      DealerCache.clear();
      
      await DealerManager.initializeFromGlobalSettings();
      
      // Check if sales pages only mode is enabled
      const salesPagesOnly = await DealerManager.isSalesPagesOnly();
      if (salesPagesOnly) {
        const { shouldLoadChat } = await import('./utils/salesPageDetector');
        const canLoad = shouldLoadChat(true);
        
        if (!canLoad) {
          logger.log('🚫 Widget blocked: sales_pages_only is enabled and current page is not a sales page');
          return false;
        }
        logger.log('✅ Widget allowed: Current page matches sales page pattern');
      }
      
      logger.log('✅ Dealer authentication successful');
      return true;
    } catch (error) {
      console.error('❌ Dealer authentication failed:', error);
      
      // Check if this is actually an unauthorized domain or just a technical error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isUnauthorizedDomain = errorMessage.includes('Unauthorized domain') || 
                                  errorMessage.includes('Domain not authorized') ||
                                  errorMessage.includes('not found in authorized dealers');
      
      if (isUnauthorizedDomain) {
        logger.log('🚨 UNAUTHORIZED DOMAIN - Sending alert and blocking widget');
        await this.sendUnauthorizedDomainAlert(window.location.hostname);
      } else {
        logger.log('🚨 TECHNICAL ERROR - Blocking widget without sending false alert');
      }
      
      // Return false to completely block widget initialization
      return false;
    }
  }

  private init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.render());
    } else {
      this.render();
    }
  }

  private render() {
    // Create container if it doesn't exist
    this.container = document.getElementById(this.config.containerId!);
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = this.config.containerId!;
      this.container.style.position = 'fixed';
      this.container.style.zIndex = '9999';
      
      // Position the widget
      switch (this.config.position) {
        case 'bottom-right':
          this.container.style.bottom = '0';
          this.container.style.right = '0';
          break;
        case 'bottom-left':
          this.container.style.bottom = '0';
          this.container.style.left = '0';
          break;
        case 'top-right':
          this.container.style.top = '0';
          this.container.style.right = '0';
          break;
        case 'top-left':
          this.container.style.top = '0';
          this.container.style.left = '0';
          break;
      }
      
      document.body.appendChild(this.container);
    }

    // Mount a sibling container for Radix portals so they receive scoped widget styles.
    // Tailwind is built with `important: '.d2cbot-container'`, so portal content rendered
    // into document.body would otherwise lose all utility classes.
    let portalRoot = document.getElementById('d2cbot-portal-root') as HTMLElement | null;
    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = 'd2cbot-portal-root';
      // `chat-widget` is required so the scoped preflight replacement in
      // index.css (box-sizing, border reset, word wrapping) also applies to
      // portalled content — without it dialogs/tooltips lose those base rules.
      portalRoot.className = `d2cbot-container chat-widget ${this.config.theme === 'dark' ? 'd2cbot-dark' : ''}`.trim();
      document.body.appendChild(portalRoot);
    }

    // Render the React app with initial language.
    // Reuse the existing root if this container was already mounted, rather than
    // calling createRoot() on it twice (which React 19 warns about and which leaves
    // two independent React trees writing to the same shared global store).
    if (activeRoot && activeContainerId === this.config.containerId) {
      logger.log('⚠️ Widget already mounted on this container — reusing existing React root');
      this.root = activeRoot;
    } else {
      this.root = createRoot(this.container);
      activeRoot = this.root;
      activeContainerId = this.config.containerId!;
    }
    const root = this.root;
    root.render(
      <QueryClientProvider client={this.queryClient}>
        <PortalRootContext.Provider value={portalRoot}>
          <TooltipProvider>
            <DealerSessionProvider>
              <LanguageProvider initialLanguage={this.initialLanguage}>
                <FormProvider>
                  <div className={`d2cbot-container chat-widget ${this.config.theme === 'dark' ? 'd2cbot-dark' : ''}`}>
                    <ChatInterface />
                  </div>
                </FormProvider>
              </LanguageProvider>
            </DealerSessionProvider>
          </TooltipProvider>
        </PortalRootContext.Provider>
      </QueryClientProvider>
    );
  }

  private async sendUnauthorizedDomainAlert(domain: string) {
    try {
      logger.log('📧 Sending unauthorized domain alert for:', domain);
      
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Try to get session ID for better tracking
      let sessionId = 'Unknown';
      try {
        const { getCurrentSession } = await import('@/utils/sessionUtils');
        const session = await getCurrentSession();
        sessionId = session?.sessionId || 'Unknown';
      } catch (error) {
        logger.warn('Could not retrieve session ID for domain alert:', error);
      }
      
      const response = await supabase.functions.invoke('send-error-email', {
        body: {
          to: 'ddodge@carscommerce.inc',
          subject: 'D2C Chatbot Error Report',
          error: 'Unauthorized domain access attempt',
          sentMessage: `Domain: ${domain}`,
          receivedMessage: 'The D2C Chatbot is currently being attempted to be installed on an unauthorized domain. No matching dealer configuration was found in the Supabase d2c_chatbot_dealers table.',
          dealerId: 'Unknown',
          sessionId: sessionId,
          timestamp: new Date().toISOString()
        }
      });
      
      if (response.error) {
        console.error('Failed to send unauthorized domain alert:', response.error);
      } else {
        logger.log('✅ Unauthorized domain alert sent successfully');
      }
    } catch (error) {
      console.error('❌ Error sending unauthorized domain alert:', error);
    }
  }

  public destroy() {
    // Unmount the React tree before detaching the container, otherwise the tree
    // keeps running (timers, intervals, store subscriptions and event listeners all
    // stay live) against DOM nodes that are no longer in the document.
    if (this.root) {
      try {
        this.root.unmount();
      } catch (error) {
        logger.warn('Failed to unmount widget React root:', error);
      }
      if (activeRoot === this.root) {
        activeRoot = null;
        activeContainerId = null;
      }
      this.root = null;
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// Make it available globally
declare global {
  interface Window {
    ChatWidget: typeof ChatWidget;
    initChatWidget: (config?: ChatWidgetConfig) => ChatWidget;
  }
}

window.ChatWidget = ChatWidget;
window.initChatWidget = (config?: ChatWidgetConfig) => new ChatWidget(config);

// Auto-initialize if data attributes are present
document.addEventListener('DOMContentLoaded', () => {
  const scriptTag = document.querySelector('script[data-chat-widget]');
  if (scriptTag) {
    const config: ChatWidgetConfig = {};
    
    // Read configuration from data attributes
    if (scriptTag.getAttribute('data-position')) {
      config.position = scriptTag.getAttribute('data-position') as any;
    }
    if (scriptTag.getAttribute('data-theme')) {
      config.theme = scriptTag.getAttribute('data-theme') as any;
    }
    if (scriptTag.getAttribute('data-container-id')) {
      config.containerId = scriptTag.getAttribute('data-container-id') as string;
    }
    
    new ChatWidget(config);
  }
});

export { ChatWidget };
